<template>
  <div v-if="!editing" class="hoveredit">
    <div class="d-flex justify-content-between">
      <b-form-checkbox
        v-if="!isTemplate"
        v-model="item.isCompleted"
        :value="true"
        :unchecked-value="false"
        @change="updateIsCompletedState($event)"
        :disabled="readOnly"
      >
        <div
          class="font-weight-bold ml-1 mb-0 cursor-pointer"
          :class="{ completed: item.isCompleted }"
          v-linkify-directive="item.title"
          @click="handleTitleClick"
        ></div>
      </b-form-checkbox>

      <div v-else class="font-weight-bold mb-0 cursor-pointer" @click="startEditItem">
        <i class="far fa-caret-right"></i>
        {{ item.title }}
      </div>

      <div class="d-flex flex-1">
        <div class="edit mr-2">
          <i
            class="displayonhover far fa-arrow-alt-up mr-2 cursor-pointer"
            v-if="itemIndex_ !== 1 && !readOnly"
            @click="changeSortOrder(-1)"
          ></i>
          <i
            class="displayonhover far fa-arrow-alt-down mr-2 cursor-pointer"
            v-if="itemIndex_ < itemcount"
            @click="changeSortOrder(+1)"
          ></i>
          <i class="far fa-pencil" v-if="!readOnly" @click="startEditItem()"></i>
        </div>

        <div class="">
          <i class="far fa-fw fa-info-circle" v-b-tooltip.hover="{ title: itemToolTip, html: true }"></i>
        </div>
      </div>
    </div>

    <div
      class="cl-item-body italic"
      v-linkify-directive="item.description"
      v-html="$options.filters.formatLineBreaks(item.description)"
    ></div>
  </div>
  <div v-else>
    <form class="edit-form" @submit.prevent="updateItem">
      <div class="label mt-1">Title</div>
      <input :disabled="!item.isEditable" type="text" ref="title" class="form-control form-control-sm" v-model="item.title" />

      <div class="label mt-2">Description / Outcome</div>
      <textarea-autosize rows="4" class="form-control form-control-sm" v-model="item.description" />

      <div class="d-flex justify-content-between mt-2 mb-2">
        <div>
          <button type="submit" class="btn btn-sm btn-success mr-2" style="width: 60px" :disabled="!item.title">
            <i class="far fa-save"></i>
            Save
          </button>
          <button class="btn btn-sm btn-info" style="width: 60px" @click="cancelEdit">Cancel</button>
        </div>

        <div @click="removeItem()" v-if="item.isEditable">
          <i class="far fa-trash-alt text-danger cursor-pointer"></i>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { ChecklistModule, ChecklistStore } from '@/store/checklist';
  import { ConfirmModal } from '@/helpers/confirm-modal';
  import { ChecklistItemDto } from '@/apiclient/apiclient_generated';

  import { Prop } from 'vue-property-decorator';
  import formatDateTime from '@/filters/formatDateTime';

  @Component({})
  export default class ChecklistItem extends Vue {
    @Prop() item: ChecklistItemDto;
    @Prop() itemIndex: number;
    @Prop() boardItemId: string;
    @Prop() isTemplate: boolean;
    @Prop() itemcount: number;
    @Prop() readOnly: boolean;
    private checklistStore: ChecklistModule = ChecklistStore;
    private editing = false;
    private newItemState: ChecklistItemDto;

    mounted() {
      this.resetNewItemState();
    }

    get itemIndex_() {
      return this.itemIndex + 1;
    }

    get itemToolTip() {
      let tooltip = '';
      if (this.item.createdDate) {
        tooltip = `<strong>Created</strong><br>${formatDateTime(this.item.createdDate)}`;

        if (this.item.createdUser) {
          tooltip += `<br>by ${this.item.createdUser}`;
        }
        tooltip += '<br>';
      }

      if (this.item.updatedDate) {
        tooltip += `<strong>Last Updated</strong><br>${formatDateTime(this.item.updatedDate)}`;

        if (this.item.updatedUser) {
          tooltip += `<br>by ${this.item.updatedUser}`;
        }
        tooltip += '<br>';
      }

      if (this.item.completedDate) {
        tooltip += `<strong>Completed</strong><br>${formatDateTime(this.item.completedDate)}`;
        if (this.item.completedUser) {
          tooltip += `<br>by ${this.item.completedUser}`;
        }
      }

      return tooltip;
    }

    resetNewItemState() {
      this.newItemState = {} as ChecklistItemDto;
      this.editing = false;
    }

    async startEditItem() {
      if (this.readOnly) return;

      this.editing = true;
      this.newItemState.title = this.item.title;
      this.newItemState.description = this.item.description;

      await this.$nextTick();
      (this.$refs.title as HTMLInputElement).focus();
    }

    cancelEdit() {
      this.item.title = this.newItemState.title;
      this.item.description = this.newItemState.description;

      this.resetNewItemState();
    }

    async updateIsCompletedState(isCompleted) {
      this.item.isCompleted = isCompleted;

      await this.updateItem();
    }

    async updateItem() {
      const params = { boardItemId: this.boardItemId, item: this.item };
      await this.checklistStore.updateChecklistItem(params);

      this.resetNewItemState();
    }

    async removeItem() {
      const params = {
        checklistId: this.item.checklistId,
        boardItemId: this.boardItemId,
        checklistItemId: this.item.id,
      };

      const success = () => {
        this.checklistStore.deleteChecklistItem(params);
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to delete this checklist item?`,
        'Confirm delete',
        'Yes, delete',
        'No',
        success,
        null
      );
    }

    async changeSortOrder(direction: number) {
      const params = {
        boardItemId: this.boardItemId,
        checklistItemId: this.item.id,
        direction: direction,
      };
      await this.checklistStore.changeItemSortOrder(params);
    }

    handleTitleClick(event: MouseEvent) {
      // Prevent checkbox toggle when clicking on a link
      if ((event.target as HTMLElement).tagName === 'A') {
        event.stopPropagation();
      }
    }
  }
</script>

<style scoped lang="scss">
  .mb-0 {
    margin-bottom: 0 !important;
  }
  .area {
    width: 100%;

    &:not(:focus) {
      border: none;
      background: inherit;
    }
  }

  .completed {
    text-decoration: line-through;
  }

  .delete-checklist {
    width: 75px;
    text-align: center;
    height: 80%;
  }

  .label {
    text-transform: none !important;
  }

  .edit-form {
    padding: 8px;
    background: lighten(#eaeaea, 2%);
    margin-bottom: 4px;
    border-radius: 4px;
  }

  .completed-tooltip {
    display: flex;
    flex: 1;
    justify-content: flex-end;
    padding-top: 3px;
  }

  .cl-item-body {
    max-width: 470px;
    overflow-wrap: anywhere;
  }

  .edit i {
    color: #888;
  }

  //.hoveredit {
  //
  //  .displayonhover {
  //    transition: opacity .1s ease-in-out !important;
  //    //visibility: hidden;
  //    opacity: 0;
  //  }
  //
  //  &:hover {
  //    .displayonhover {
  //      //visibility: visible;
  //      opacity: 1;
  //    }
  //  }
  //}
</style>
